import React, { useState, useEffect, useCallback } from 'react';
import { useStore, setState, getState } from './hooks/useStore.js';
import { 
  initAuth, 
  getCurrentAuthUser, 
  getStoredAuthSession,
  signOutUser, 
  isAuthorizedUser,
  getAuthenticatedOperatorName
} from './services/auth.js';
import { Loader2 } from 'lucide-react';
import { getSupabaseClient } from './services/supabase.js';
import { 
  startRealtimeSync, 
  stopRealtimeSync, 
  onRealtimeStatusChange 
} from './services/realtime.js';
import { loadLocalData } from './state/storage.js';
import { syncWithSupabase, syncAfterLocalChange } from './services/sync.js';
import { removeInventoryItem } from './state/store.js';
import { rpcDeleteProductLots } from './services/rpc.js';
import { hasCloudSyncConfig } from './services/supabase.js';
import { getProductGroupKey } from './utils/sorting.js';
import { parseSearchTokens, matchesSearchTokens, getItemSearchToken } from './utils/search.js';
import { getDaysUntilExpiry, getExpiryStatus } from './utils/date.js';
import { generateInventoryReportHtml, generateBarcodeLabelsHtml, triggerPrint } from './utils/print.js';

// Layout & Auth
import { Header } from './components/layout/Header.jsx';
import { LoginBarrier } from './components/auth/LoginBarrier.jsx';

// Views
import { InventoryToolbar } from './components/inventory/InventoryToolbar.jsx';
import { InventoryVirtualTable } from './components/inventory/InventoryVirtualTable.jsx';
import { TransactionsView } from './components/transactions/TransactionsView.jsx';
import { AuditView } from './components/audit/AuditView.jsx';
import { StockOutView } from './components/stock-out/StockOutView.jsx';
import { SettingsView } from './components/settings/SettingsView.jsx';

// Modals
import { ItemModal } from './components/modals/ItemModal.jsx';
import { ItemDetailModal } from './components/modals/ItemDetailModal.jsx';
import { BorrowModal } from './components/modals/BorrowModal.jsx';
import { ReturnModal } from './components/modals/ReturnModal.jsx';
import { StockOutModal } from './components/modals/StockOutModal.jsx';
import { AlertModal } from './components/ui/AlertModal.jsx';

export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [syncStatusText, setSyncStatusText] = useState('');

  const activeTab = useStore(s => s.activeTab) || 'inventory';
  const inventory = useStore(s => s.inventory) || [];
  const selectedInventoryIds = useStore(s => s.selectedInventoryIds) || new Set();

  // Modals state
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [viewingItemId, setViewingItemId] = useState(null);

  const [borrowModalOpen, setBorrowModalOpen] = useState(false);
  const [borrowPreselectedKeys, setBorrowPreselectedKeys] = useState([]);

  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returnPreselectedTxId, setReturnPreselectedTxId] = useState(null);

  const [stockOutModalOpen, setStockOutModalOpen] = useState(false);
  const [stockOutPreselectedKeys, setStockOutPreselectedKeys] = useState([]);

  // Confirm / Alert Modal
  const [alertState, setAlertState] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null
  });

  const handleAuthenticated = useCallback((authUser) => {
    if (authUser && isAuthorizedUser(authUser)) {
      setCurrentUser(authUser);
      startRealtimeSync();
      syncWithSupabase({ forcePull: true }).catch(err => console.warn('Sync on login note:', err));
    }
  }, []);

  // 1. Initial boot: Auth & Local Storage
  useEffect(() => {
    loadLocalData();

    onRealtimeStatusChange((statusText, isConnected) => {
      setSyncStatusText(statusText);
      setRealtimeConnected(isConnected);
    });

    async function checkAuth() {
      try {
        const client = getSupabaseClient();
        const res = await initAuth({ client });
        const authUser = res?.user || getCurrentAuthUser() || getStoredAuthSession()?.user;
        if (authUser && isAuthorizedUser(authUser)) {
          setCurrentUser(authUser);
          startRealtimeSync();
          syncWithSupabase({ forcePull: true }).catch(err => console.warn('Initial cloud sync note:', err));
        }
      } catch (err) {
        console.warn('initAuth boot error:', err);
      } finally {
        setAuthInitialized(true);
      }
    }

    checkAuth();

    return () => {
      stopRealtimeSync();
    };
  }, []);

  const handleSignOut = async () => {
    try {
      stopRealtimeSync();
    } catch (e) {}
    const client = getSupabaseClient();
    await signOutUser(client);
    setCurrentUser(null);
  };

  // 2. Action Handlers for Inventory
  const handleAddItem = () => {
    setEditingItemId(null);
    setItemModalOpen(true);
  };

  const handleEditItem = (itemId) => {
    setEditingItemId(itemId);
    setItemModalOpen(true);
  };

  const handleViewDetails = (itemId) => {
    setViewingItemId(itemId);
    setDetailModalOpen(true);
  };

  const handleBorrowItem = (productKey) => {
    setBorrowPreselectedKeys(productKey ? [productKey] : []);
    setBorrowModalOpen(true);
  };

  const handleBatchBorrow = () => {
    if (selectedInventoryIds.size === 0) return;
    setBorrowPreselectedKeys(Array.from(selectedInventoryIds));
    setBorrowModalOpen(true);
  };

  const handleBatchStockOut = () => {
    if (selectedInventoryIds.size === 0) return;
    setStockOutPreselectedKeys(Array.from(selectedInventoryIds));
    setStockOutModalOpen(true);
  };

  const handleDeleteProduct = (productKey, productName) => {
    setAlertState({
      isOpen: true,
      type: 'danger',
      title: 'ยืนยันการลบสินค้า',
      message: `คุณต้องการลบ "${productName}" ทุกรายการล็อตย่อยใช่หรือไม่?`,
      confirmText: 'ยืนยันลบ',
      cancelText: 'ยกเลิก',
      onCancel: () => setAlertState(s => ({ ...s, isOpen: false })),
      onConfirm: async () => {
        setAlertState(s => ({ ...s, isOpen: false }));
        if (hasCloudSyncConfig()) {
          try {
            await rpcDeleteProductLots(productKey);
          } catch (rpcErr) {
            console.warn('RPC delete product lots fallback to local soft-delete:', rpcErr);
          }
        }
        const activeItems = inventory.filter(item => !item.deleted);
        activeItems.forEach(lot => {
          if (getProductGroupKey(lot) === productKey) {
            removeInventoryItem(lot.id);
          }
        });
        syncAfterLocalChange('delete-product');
      }
    });
  };

  // 3. Print Actions
  const handlePrintReport = () => {
    try {
      const currentState = getState();
      const currentInventory = currentState.inventory || [];
      const currentSelectedIds = currentState.selectedInventoryIds || new Set();
      const currentLoc = (currentState.locationFilter || 'all').trim();
      const currentFilter = currentState.filter || 'all';
      const currentQuery = (currentState.searchQuery || '').trim();
      const appSettings = currentState.settings || {};

      let itemsToPrint = currentInventory.filter(item => !item.deleted);

      // If specific items are checked, only print those items
      if (currentSelectedIds.size > 0) {
        itemsToPrint = itemsToPrint.filter(item => currentSelectedIds.has(getProductGroupKey(item)));
      } else {
        // Location filter
        if (currentLoc && currentLoc !== 'all') {
          itemsToPrint = itemsToPrint.filter(item => String(item.location || '').trim() === currentLoc);
        }

        // Search query filter (Multi-Token AND Match)
        if (currentQuery) {
          const searchTokens = parseSearchTokens(currentQuery);
          itemsToPrint = itemsToPrint.filter(item => matchesSearchTokens(getItemSearchToken(item), searchTokens));
        }

        // Dropdown Filter (low, expired, near-expiry)
        const threshold = Number(appSettings.lowStockThreshold) || 5;
        if (currentFilter === 'low') {
          const groupSums = {};
          currentInventory.forEach(item => {
            if (item.deleted) return;
            const key = getProductGroupKey(item);
            groupSums[key] = (groupSums[key] || 0) + Number(item.quantity || 0);
          });
          itemsToPrint = itemsToPrint.filter(item => {
            const key = getProductGroupKey(item);
            return (groupSums[key] || 0) <= threshold;
          });
        } else if (currentFilter === 'expired') {
          itemsToPrint = itemsToPrint.filter(item => {
            if (!item.expiryDate) return false;
            const days = getDaysUntilExpiry(item.expiryDate);
            return days !== null && days <= 0;
          });
        } else if (currentFilter === 'near-expiry') {
          itemsToPrint = itemsToPrint.filter(item => {
            if (!item.expiryDate) return false;
            const status = getExpiryStatus(item.expiryDate, appSettings.expiryWarningDate);
            return status === 'expiring-soon' || status === 'expired';
          });
        }
      }

      const operatorName = currentUser?.user_metadata?.full_name || 
                           currentUser?.user_metadata?.name || 
                           getAuthenticatedOperatorName() || 
                           currentUser?.email || 
                           'เจ้าหน้าที่คลัง SDR';

      const locationLabel = currentSelectedIds.size > 0 
        ? 'เฉพาะรายการที่เลือก' 
        : (currentLoc === 'all' ? 'ทุกสถานที่ / ตู้จัดเก็บ' : currentLoc);

      const filterLabel = currentSelectedIds.size > 0 
        ? 'เฉพาะรายการที่เลือก' 
        : currentFilter;

      const html = generateInventoryReportHtml({
        items: itemsToPrint,
        locationFilter: locationLabel,
        filter: filterLabel,
        searchQuery: currentSelectedIds.size > 0 ? '' : currentQuery,
        expiryWarningDate: appSettings.expiryWarningDate || '',
        operatorName,
        printDate: new Date()
      });

      triggerPrint(html);
    } catch (e) {
      alert('ไม่สามารถเปิดหน้าต่างพิมพ์รายงานได้: ' + e.message);
    }
  };

  const handlePrintLabels = () => {
    try {
      const currentState = getState();
      const currentInventory = currentState.inventory || [];
      const currentSelectedIds = currentState.selectedInventoryIds || new Set();

      let activeItems = currentInventory.filter(i => !i.deleted);
      if (currentSelectedIds.size > 0) {
        activeItems = activeItems.filter(i => currentSelectedIds.has(getProductGroupKey(i)));
      }

      const html = generateBarcodeLabelsHtml(activeItems);
      triggerPrint(html);
    } catch (e) {
      alert('ไม่สามารถพิมพ์ป้าย Barcode ได้: ' + e.message);
    }
  };

  if (!authInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 select-none">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center overflow-hidden shadow-lg">
            <img
              src="sdr-logo.png"
              alt="SDR Logo"
              className="w-12 h-12 object-contain"
              onError={(e) => {
                if (!e.currentTarget.dataset.fallback) {
                  e.currentTarget.dataset.fallback = '1';
                  e.currentTarget.src = 'assets/sdr-logo.png';
                } else {
                  e.currentTarget.src = 'sdr-logo-favicon.png';
                }
              }}
            />
          </div>
          <div className="flex items-center gap-2 text-indigo-300 font-medium text-sm">
            <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
            <span>กำลังตรวจสอบสิทธิ์การเข้าสู่ระบบ...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <LoginBarrier onAuthenticated={handleAuthenticated} />;
  }

  const viewingItem = inventory.find(i => i.id === viewingItemId && !i.deleted) || null;

  return (
    <div className="min-h-screen flex flex-col subpixel-antialiased text-slate-900 touch-scroll">
      {/* Top Application Header */}
      <Header
        user={currentUser}
        onSignOut={handleSignOut}
        realtimeConnected={realtimeConnected}
        syncStatusText={syncStatusText}
      />

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-3 sm:px-4 md:px-5 lg:px-6 py-4 sm:py-6 touch-scroll">
        {activeTab === 'inventory' && (
          <div>
            <InventoryToolbar
              onAddItem={handleAddItem}
              onBatchBorrow={handleBatchBorrow}
              onBatchStockOut={handleBatchStockOut}
              onPrintReport={handlePrintReport}
              onPrintLabels={handlePrintLabels}
            />
            <InventoryVirtualTable
              onEditItem={handleEditItem}
              onBorrowItem={handleBorrowItem}
              onDeleteProduct={handleDeleteProduct}
              onViewDetails={handleViewDetails}
            />
          </div>
        )}

        {activeTab === 'transactions' && (
          <TransactionsView
            onOpenReturnModal={(txId) => {
              setReturnPreselectedTxId(txId);
              setReturnModalOpen(true);
            }}
          />
        )}

        {activeTab === 'audit' && <AuditView />}

        {activeTab === 'stock-out' && (
          <StockOutView
            onOpenStockOutModal={() => {
              setStockOutPreselectedKeys([]);
              setStockOutModalOpen(true);
            }}
          />
        )}

        {activeTab === 'settings' && <SettingsView />}
      </main>

      {/* Modals */}
      <ItemModal
        isOpen={itemModalOpen}
        itemId={editingItemId}
        onClose={() => setItemModalOpen(false)}
      />

      <ItemDetailModal
        isOpen={detailModalOpen}
        item={viewingItem}
        onClose={() => setDetailModalOpen(false)}
        onEditItem={handleEditItem}
        onBorrowItem={handleBorrowItem}
      />

      <BorrowModal
        isOpen={borrowModalOpen}
        preselectedProductKeys={borrowPreselectedKeys}
        onClose={() => setBorrowModalOpen(false)}
      />

      <ReturnModal
        isOpen={returnModalOpen}
        preselectedBorrowTxId={returnPreselectedTxId}
        onClose={() => setReturnModalOpen(false)}
      />

      <StockOutModal
        isOpen={stockOutModalOpen}
        preselectedProductKeys={stockOutPreselectedKeys}
        onClose={() => setStockOutModalOpen(false)}
      />

      <AlertModal
        isOpen={alertState.isOpen}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        onConfirm={alertState.onConfirm}
        onCancel={alertState.onCancel}
      />
    </div>
  );
}
